import { GVL, TCModel, TCString } from '@iabtcf/core';
import { getTCStringFromCookie } from './cookieutils';

import config, { Config, PurposeIds, CustomPurposeIds } from './config';
import gvl from './gvl';

export function isValidTCString(tcString: string): boolean {
  return Boolean(tcString && tcString.startsWith('C'));
}

// Creates a TCModel which holds user consent settings as object.
//
// If a valid tcstring is found, it's decoded.
// If an invalid tcstring is found, a new TC model with default settings is
// created.
//
// The default consent configuration was decided by WeTransfer as a publisher.
function createTCModel(tcString: string, gvl: GVL, config: Config): TCModel {
  let tcModel = new TCModel(gvl);

  if (tcString && isValidTCString(tcString)) {
    tcModel = TCString.decode(tcString, tcModel);
  } else {
    tcModel.purposeLegitimateInterests.set([
      PurposeIds.SELECT_BASIC_ADS,
      PurposeIds.MEASURE_AD_PERFORMANCE,
      PurposeIds.DEVELOP_IMPROVE_PRODUCTS,
    ]);
  }

  tcModel.cmpId = config.cmpId;
  tcModel.cmpVersion = config.cmpVersion;

  return tcModel;
}

const tcModel = createTCModel(getTCStringFromCookie(), gvl, config);

export function getTCString() {
  return TCString.encode(tcModel);
}

export function hasRejectedAll() {
  return (
    tcModel.purposeConsents.size === 0 &&
    tcModel.purposeLegitimateInterests.size === 0
  );
}

// In WeTransfer's context, a TC model has full consent when all 3 consents are
// checked
export function hasFullConsent() {
  return (
    tcModel.purposeConsents.has(PurposeIds.SELECT_BASIC_ADS) &&
    tcModel.purposeConsents.has(PurposeIds.MEASURE_AD_PERFORMANCE) &&
    tcModel.purposeConsents.has(CustomPurposeIds.PROMOTE_PRODUCTS)
  );
}

export function hasAcceptedAllVendorConsent() {
  return tcModel.vendorConsents.size === config.vendorIds.length;
}

export function hasPartialConsent() {
  return !hasRejectedAll() && !hasFullConsent();
}

export function shouldAllowAllVendors() {
  return hasFullConsent() && tcModel.vendorConsents.size === 0;
}

export default tcModel;
